/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Tag, Copy, Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'

/** Single source of truth for the tags the send pipeline actually replaces. */
export const MERGE_TAG_REFERENCE = [
  { tag: '{{firstName}}', label: 'Recipient first name', sample: 'Jane' },
  { tag: '{{lastName}}', label: 'Recipient last name', sample: 'Smith' },
  { tag: '{{company}}', label: "Recipient's company", sample: 'Acme Ltd' },
  { tag: '{{companyName}}', label: 'Your company (sending profile)', sample: 'NXAPP Limited' },
  { tag: '{{companyWebsite}}', label: 'Your website (sending profile)', sample: 'nxapp.ai' },
  { tag: '{{companyAddress}}', label: 'Your address (sending profile)', sample: '128 City Road, London' },
  { tag: '{{UNSUBSCRIBE_URL}}', label: 'Unsubscribe link (body only)', sample: 'https://…' },
] as const

/**
 * Expandable reference for merge tags. Shown next to subject lines, where the
 * editor's own tag menu is not available.
 */
export default function MergeTagHelp({ onInsert }: { onInsert?: (tag: string) => void }) {
  const { user } = useAuthContext()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  // Custom fields the user created on CSV import — usable as {{key}} tags too.
  const { data: customTags } = useQuery({
    queryKey: ['custom-fields', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('contact_custom_fields').select('key, label').eq('user_id', user!.id).order('label')
      return (data ?? []).map(f => ({ tag: `{{${f.key}}}`, label: (f.label as string) || (f.key as string) }))
    },
  })

  const handle = (tag: string) => {
    if (onInsert) { onInsert(tag); return }
    navigator.clipboard?.writeText(tag)
    setCopied(tag)
    setTimeout(() => setCopied(null), 1500)
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-brand-600 transition-colors"
      >
        <Tag className="w-3 h-3" />
        {open ? 'Hide personalisation tags' : 'Personalisation tags'}
      </button>

      {open && (
        <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 p-2.5">
          <p className="text-[11px] text-gray-500 mb-2">
            Type these anywhere in the subject or the email — they're swapped for the
            recipient's details when it sends. {onInsert ? 'Click to insert.' : 'Click to copy.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {MERGE_TAG_REFERENCE.map(t => (
              <button
                key={t.tag}
                type="button"
                onClick={() => handle(t.tag)}
                className="flex items-center gap-2 text-left py-0.5 group/tag"
                title={onInsert ? 'Insert' : 'Copy'}
              >
                <code className="text-[11px] font-mono text-brand-700 bg-white border border-gray-200 rounded px-1 py-0.5 whitespace-nowrap">
                  {t.tag}
                </code>
                <span className="text-[11px] text-gray-500 truncate flex-1">{t.label}</span>
                {copied === t.tag
                  ? <Check className="w-3 h-3 text-green-600 shrink-0" />
                  : <Copy className="w-3 h-3 text-gray-300 opacity-0 group-hover/tag:opacity-100 shrink-0" />}
              </button>
            ))}
          </div>

          {!!customTags?.length && (
            <>
              <p className="text-[11px] font-medium text-gray-500 mt-3 mb-1">Your custom fields (from imports)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {customTags.map(t => (
                  <button
                    key={t.tag}
                    type="button"
                    onClick={() => handle(t.tag)}
                    className="flex items-center gap-2 text-left py-0.5 group/tag"
                    title={onInsert ? 'Insert' : 'Copy'}
                  >
                    <code className="text-[11px] font-mono text-brand-700 bg-white border border-gray-200 rounded px-1 py-0.5 whitespace-nowrap">
                      {t.tag}
                    </code>
                    <span className="text-[11px] text-gray-500 truncate flex-1">{t.label}</span>
                    {copied === t.tag
                      ? <Check className="w-3 h-3 text-green-600 shrink-0" />
                      : <Copy className="w-3 h-3 text-gray-300 opacity-0 group-hover/tag:opacity-100 shrink-0" />}
                  </button>
                ))}
              </div>
            </>
          )}

          <p className="text-[10px] text-gray-400 mt-2">
            If a recipient has no value for a tag it becomes blank, and the spacing around
            it is tidied up — so “Hi {'{{firstName}}'},” still reads as “Hi,”.
          </p>
        </div>
      )}
    </div>
  )
}
